import { useRef } from 'react';
import { useGT } from 'gt-react';

/** How far one arrow key press widens or narrows a column, in px. */
const KEY_STEP = 10;

/**
 * The right edge of a column header, dragged to resize its column as in a
 * spreadsheet: arrows resize by 10px, a double-click fits the header. A
 * separator, so assistive tech announces it and the header stays a header.
 */
export function ColumnResizeHandle({
  header,
  width,
  onResize,
  onFit,
}: {
  header: string;
  /** The column's current width, in px as drawn. */
  width: number;
  onResize: (width: number) => void;
  onFit: () => void;
}) {
  const gt = useGT();
  const start = useRef<{ x: number; width: number } | null>(null);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(width)}
      aria-label={gt('Resize {header}', {
        header,
        context: 'xlsx viewer column resize handle',
      })}
      tabIndex={0}
      className="absolute inset-y-0 right-0 z-10 w-1.5 cursor-col-resize touch-none outline-none hover:bg-ring/40 focus-visible:bg-ring/60"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        start.current = { x: event.clientX, width };
        const move = (next: PointerEvent) => {
          if (start.current)
            onResize(start.current.width + next.clientX - start.current.x);
        };
        const up = () => {
          start.current = null;
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      }}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onFit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') onResize(width + KEY_STEP);
        else if (event.key === 'ArrowLeft') onResize(width - KEY_STEP);
        else return;
        event.preventDefault();
      }}
    />
  );
}
